import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import OrderService from '../services/order.service';
import { ArrowUpRight } from 'lucide-react';
import { formatPrice } from '../utils/currency';

const MyOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const response = await OrderService.getOrders();
            setOrders(response.data.results || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'COMPLETED': return 'bg-green-50 text-green-700 border-green-200';
            case 'PENDING': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
            case 'CANCELLED': return 'bg-red-50 text-red-700 border-red-200';
            default: return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-20 min-h-[60vh]">
            <div className="flex items-end justify-between mb-12">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Order History</h1>
                    <p className="text-gray-500 text-sm font-light">Track and manage your recent purchases.</p>
                </div>
                <Link to="/" className="text-xs uppercase tracking-widest hover:underline underline-offset-4 hidden md:block">
                    Continue Shopping
                </Link>
            </div>

            {loading ? (
                <div className="text-center py-20 text-gray-400 text-sm uppercase tracking-widest">Loading orders...</div>
            ) : (
                <div className="w-full overflow-x-auto">
                    {orders.length === 0 ? (
                        <div className="text-center py-20 bg-gray-50 border border-gray-100 rounded-sm">
                            <p className="text-gray-500 mb-4 uppercase tracking-widest text-xs">No orders found</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs uppercase tracking-widest text-gray-400">
                                    <th className="py-4 font-normal">Order ID</th>
                                    <th className="py-4 font-normal">Date</th>
                                    <th className="py-4 font-normal">Status</th>
                                    <th className="py-4 font-normal text-right">Total</th>
                                    <th className="py-4 font-normal text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm">
                                {orders.map(order => (
                                    <tr key={order.id} className="border-b border-gray-100 group hover:bg-gray-50 transition-colors">
                                        <td className="py-6 font-medium font-sans text-lg">
                                            #{order.order_number || order.id.toString().slice(0, 8)}
                                        </td>
                                        <td className="py-6 text-gray-500 font-light font-sans">
                                            {new Date(order.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </td>
                                        <td className="py-6">
                                            <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold border ${getStatusColor(order.status)}`}>
                                                {order.status}
                                            </span>
                                        </td>
                                        <td className="py-6 text-right font-medium font-sans">
                                            {formatPrice(order.total)}
                                        </td>
                                        <td className="py-6 text-right">
                                            <Link to={`/orders/${order.id}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest hover:text-black text-gray-400 transition-colors group-hover:translate-x-1 duration-300">
                                                View Details <ArrowUpRight size={14} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};

export default MyOrders;
